import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  TextField,
} from "@mui/material";
import Popup from "devextreme-react/popup";
import { TextBox } from "devextreme-react";
import { showNotification } from "./NotificationProvider";

import { useDispatch, useSelector } from "react-redux";
import {
  getPendingPayments,
  approvePayment,
  rejectPayment,
  clearLastAction,
} from "../store";

const PendingPayments = () => {
  const dispatch = useDispatch();
  const pendingPayments = useSelector(
    (state) => state.teamPayment.pendingPayments
  );
  const isSubmitting = useSelector((state) => state.teamPayment.isSubmitting);
  const lastAction = useSelector((state) => state.teamPayment.lastAction);

  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchPendingPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      lastAction === "payment_approved" ||
      lastAction === "payment_rejected"
    ) {
      fetchPendingPayments();
      dispatch(clearLastAction());
    }
  }, [lastAction, dispatch]);

  const fetchPendingPayments = async () => {
    try {
      await dispatch(getPendingPayments()).unwrap();
    } catch (error) {
      console.error("Failed to fetch pending payments:", error);
      showNotification("Error", "error", "Failed to fetch pending payments.");
    }
  };

  const handleApprove = async (payment) => {
    try {
      await dispatch(
        approvePayment({
          paymentId: payment.paymentId,
          periodId: payment.periodId,
        })
      ).unwrap();
      showNotification("Success", "success", "Payment approved successfully.");
    } catch (error) {
      console.error("Failed to approve payment:", error);
      showNotification("Error", "error", error || "Failed to approve payment.");
    }
  };

  const handleRejectClick = (payment) => {
    setSelectedPayment(payment);
    setRejectPopupVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!selectedPayment) return;

    try {
      await dispatch(
        rejectPayment({
          paymentId: selectedPayment.paymentId,
          periodId: selectedPayment.periodId,
          rejectionReason: rejectionReason || undefined,
        })
      ).unwrap();
      showNotification("Success", "success", "Payment rejected successfully.");
      setRejectPopupVisible(false);
      setRejectionReason("");
      setSelectedPayment(null);
    } catch (error) {
      console.error("Failed to reject payment:", error);
      showNotification("Error", "error", error || "Failed to reject payment.");
    }
  };

  const renderRejectPopup = () => (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <label
          style={{
            color: "#E0E6ED",
            fontSize: "1.3rem",
            marginBottom: "0.5rem",
            display: "block",
          }}
        >
          Rejection Reason (Optional)
        </label>
        <TextBox
          value={rejectionReason}
          onValueChanged={(e) => setRejectionReason(e.value)}
          placeholder="Enter reason for rejection"
          width="100%"
        />
      </div>
      <Button
        variant="contained"
        color="error"
        onClick={handleRejectSubmit}
        disabled={isSubmitting}
        sx={{
          marginTop: "1rem",
          fontSize: "1.5rem",
        }}
      >
        {isSubmitting ? "Rejecting..." : "Confirm Rejection"}
      </Button>
    </div>
  );

  return (
    <div
      style={{
        padding: "2rem",
        backgroundColor: "#1C273A",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ color: "#E0E6ED", fontSize: "2rem" }}>
          Pending Payments ({pendingPayments.length})
        </h2>
      </div>

      {pendingPayments.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "4rem",
            backgroundColor: "#2E3B55",
            borderRadius: "8px",
          }}
        >
          <span style={{ color: "#757575", fontSize: "1.5rem" }}>
            No pending payments at the moment.
          </span>
        </div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#2E3B55",
            color: "#E0E6ED",
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-root": {
                fontSize: "1.2rem",
                color: "#E0E6ED",
                borderBottom: "1px solid #3A4663",
              },
              "& .MuiTableHead-root": {
                backgroundColor: "#3A4663",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Amount (USD)</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingPayments.map((payment) => (
                <TableRow key={payment.paymentId}>
                  <TableCell>
                    <span style={{ color: "#2196F3", fontWeight: "bold" }}>
                      {payment.team.teamName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${new Date(
                        payment.year,
                        payment.month - 1
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}`}
                      sx={{
                        backgroundColor: "#556CD6",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#FF9800", fontWeight: "bold" }}>
                      ${payment.amount}
                    </span>
                  </TableCell>
                  <TableCell>
                    {payment.createdBy.name} {payment.createdBy.lastName}
                    <br />
                    <span style={{ fontSize: "0.9rem", color: "#9E9E9E" }}>
                      {payment.createdBy.email}
                    </span>
                  </TableCell>
                  <TableCell>
                    {new Date(payment.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {payment.notes || (
                      <span style={{ color: "#757575" }}>No notes</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleApprove(payment)}
                      disabled={isSubmitting}
                      sx={{ marginRight: "1rem" }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleRejectClick(payment)}
                      disabled={isSubmitting}
                    >
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject Popup */}
      <Popup
        width={500}
        height={280}
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={rejectPopupVisible}
        onHiding={() => {
          setRejectPopupVisible(false);
          setRejectionReason("");
          setSelectedPayment(null);
        }}
        showCloseButton={true}
        container=".dx-viewport"
        title="Reject Payment"
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
        contentRender={renderRejectPopup}
      />
    </div>
  );
};

export default PendingPayments;




